import type { WaitingRoomEntry } from '../types';

export type WaitingRoomAdmitDestinationType = 'active_call' | 'walk_in' | 'appointment';

export interface WaitingRoomAdmitAppointmentDestination {
  appointmentId: string;
  encounterId?: string | null;
  patientName?: string | null;
  encounterType?: string | null;
  startTime?: string | null;
  durationMinutes?: number | null;
}

export interface WaitingRoomAdmitActiveCallDestination {
  encounterId: string;
  patientName: string;
}

export type WaitingRoomAdmitDestination =
  | {
      type: 'active_call';
      encounterId: string;
      label: string;
      description: string;
    }
  | {
      type: 'walk_in';
      label: string;
      description: string;
    }
  | {
      type: 'appointment';
      appointment: WaitingRoomAdmitAppointmentDestination;
      label: string;
      description: string;
    };

interface SubjectLanguageCopy {
  singular: string;
  singularCap: string;
}

const formatAppointmentTime = (startTime?: string | null) => {
  if (!startTime) {
    // Unknown timing must not read as a same-day promise (GDN-2784).
    return 'Time unavailable';
  }

  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) {
    return 'Time unavailable';
  }

  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  // A destination on another day must say so — an admit target is a
  // documentation binding, and a bare clock reads as "today".
  const isToday = date.toDateString() === new Date().toDateString();
  if (isToday) return time;
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${day}, ${time}`;
};

const getAppointmentDescription = (appointment: WaitingRoomAdmitAppointmentDestination) => {
  const time = formatAppointmentTime(appointment.startTime);
  const duration = appointment.durationMinutes ? `, ${appointment.durationMinutes} min` : '';
  const type = appointment.encounterType ? ` · ${appointment.encounterType}` : '';

  return `${time}${duration}${type}`;
};

export function buildWaitingRoomAdmitDestinations({
  entry,
  appointments = [],
  activeCall,
  subjectLanguage,
}: {
  entry: WaitingRoomEntry;
  appointments?: WaitingRoomAdmitAppointmentDestination[];
  activeCall?: WaitingRoomAdmitActiveCallDestination | null;
  subjectLanguage: SubjectLanguageCopy;
}): WaitingRoomAdmitDestination[] {
  const destinationRows: WaitingRoomAdmitDestination[] = [];

  if (activeCall) {
    destinationRows.push({
      type: 'active_call',
      encounterId: activeCall.encounterId,
      label: `Add to active visit with ${activeCall.patientName}`,
      description: `${entry.patient_name} will join the call already in progress.`,
    });
  } else {
    destinationRows.push({
      type: 'walk_in',
      label: `Start a walk-in encounter for ${entry.patient_name}`,
      description: `Create a new telehealth encounter for this ${subjectLanguage.singular}.`,
    });
  }

  destinationRows.push(
    ...appointments.map(appointment => ({
      type: 'appointment' as const,
      appointment,
      label: appointment.patientName || appointment.encounterType || 'Scheduled visit',
      description: getAppointmentDescription(appointment),
    })),
  );

  if (activeCall) {
    destinationRows.push({
      type: 'walk_in',
      label: `Start a separate walk-in encounter for ${entry.patient_name}`,
      description: `Create a new telehealth encounter instead of adding this ${subjectLanguage.singular} to the active call.`,
    });
  }

  return destinationRows;
}
